import { formatDateTime } from '../../i18n/format';

/** Local HH:mm helpers for schedule UI (mirrors API / device window rules). */

export function localTimeToMinutes(value: string): number {
  const [hours, minutes] = value.split(':').map(Number);
  return (hours ?? 0) * 60 + (minutes ?? 0);
}

export function minutesToLocalTime(total: number): string {
  const wrapped = ((total % (24 * 60)) + 24 * 60) % (24 * 60);
  const hours = Math.floor(wrapped / 60);
  const minutes = wrapped % 60;
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
}

/** Empty daysOfWeek = every day (API contract). */
export function appliesOnDay(daysOfWeek: number[], dayOfWeek: number): boolean {
  return daysOfWeek.length === 0 || daysOfWeek.includes(dayOfWeek);
}

export function isWindowActiveAtMinutes(
  startLocalTime: string,
  endLocalTime: string,
  daysOfWeek: number[],
  dayOfWeek: number,
  minutesOfDay: number,
): boolean {
  if (!appliesOnDay(daysOfWeek, dayOfWeek)) return false;
  const start = localTimeToMinutes(startLocalTime);
  const end = localTimeToMinutes(endLocalTime);
  if (start < end) return minutesOfDay >= start && minutesOfDay <= end;
  return minutesOfDay >= start || minutesOfDay <= end;
}

/** Inclusive same-day / overnight overlap on a single weekday. */
export function windowsOverlapOnDay(
  a: { startLocalTime: string; endLocalTime: string; daysOfWeek: number[] },
  b: { startLocalTime: string; endLocalTime: string; daysOfWeek: number[] },
  dayOfWeek: number,
): boolean {
  if (!appliesOnDay(a.daysOfWeek, dayOfWeek) || !appliesOnDay(b.daysOfWeek, dayOfWeek)) {
    return false;
  }
  return minuteRangesOverlap(
    expandWindowMinutes(a.startLocalTime, a.endLocalTime),
    expandWindowMinutes(b.startLocalTime, b.endLocalTime),
  );
}

function expandWindowMinutes(
  startLocalTime: string,
  endLocalTime: string,
): Array<{ start: number; end: number }> {
  const start = localTimeToMinutes(startLocalTime);
  const end = localTimeToMinutes(endLocalTime);
  if (start < end) return [{ start, end }];
  // Overnight → two segments on the same weekday model used by the timeline.
  return [
    { start, end: 24 * 60 },
    { start: 0, end },
  ];
}

function minuteRangesOverlap(
  left: Array<{ start: number; end: number }>,
  right: Array<{ start: number; end: number }>,
): boolean {
  for (const a of left) {
    for (const b of right) {
      if (a.start < b.end && b.start < a.end) return true;
    }
  }
  return false;
}

export type TimelineSegment = {
  startPct: number;
  widthPct: number;
  label: string;
};

/** Segments for one calendar day on the week timeline (handles overnight). */
export function timelineSegmentsForDay(
  startLocalTime: string,
  endLocalTime: string,
  label: string,
): TimelineSegment[] {
  const start = localTimeToMinutes(startLocalTime);
  const end = localTimeToMinutes(endLocalTime);
  const day = 24 * 60;
  if (start < end) {
    return [
      {
        startPct: (start / day) * 100,
        widthPct: ((end - start) / day) * 100,
        label,
      },
    ];
  }
  return [
    {
      startPct: (start / day) * 100,
      widthPct: ((day - start) / day) * 100,
      label,
    },
    {
      startPct: 0,
      widthPct: (end / day) * 100,
      label,
    },
  ];
}

/** Monday-start week containing `anchor` (local browser calendar). */
export function weekDaysContaining(anchor: Date): Date[] {
  const start = startOfWeekMonday(anchor);
  return Array.from({ length: 7 }, (_, index) => {
    const day = new Date(start);
    day.setDate(start.getDate() + index);
    return day;
  });
}

export function startOfWeekMonday(anchor: Date): Date {
  const day = new Date(anchor.getFullYear(), anchor.getMonth(), anchor.getDate());
  const weekday = day.getDay(); // 0 Sun … 6 Sat
  const offset = weekday === 0 ? -6 : 1 - weekday;
  day.setDate(day.getDate() + offset);
  return day;
}

export function formatWeekRange(days: Date[]): string {
  if (days.length === 0) return '';
  const first = days[0]!;
  const last = days[days.length - 1]!;
  const options = { month: 'short', day: 'numeric' } as const;
  return `${formatDateTime(first, options)} – ${formatDateTime(last, options)}`;
}

export function jsDayToContractDay(date: Date): number {
  return date.getDay(); // already 0=Sun … 6=Sat
}
